import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const UserContext = createContext(null);

export function UserProvider({ children }) {
  const [user, setUser] = useState(() => {
    // Initialize from localStorage immediately
    try {
      const cached = localStorage.getItem("markeemix_user");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [plan, setPlan] = useState(() => {
    try {
      const cached = localStorage.getItem("markeemix_user");
      return cached ? JSON.parse(cached).plan || "free" : "free";
    } catch {
      return "free";
    }
  });
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchingRef = useRef(false);

  // Logout function
  const logout = useCallback(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error("Logout error:", err);
    }
    sessionStorage.setItem("markeemix_logged_out", "true");
    setUser(null);
    setPlan("free");
    setLimits(null);
    localStorage.removeItem("markeemix_user");
    window.location.href = "/";
  }, []);

  // Set user directly (called after login/register)
  const setUserData = useCallback((userData) => {
    if (userData) {
      setUser(userData);
      setPlan(userData.plan || "free");
      localStorage.setItem("markeemix_user", JSON.stringify(userData));
    }
  }, []);

  const fetchUser = useCallback(async (force = false) => {
    // Prevent multiple simultaneous fetches
    if (fetchingRef.current && !force) return;
    
    // Skip if user intentionally logged out
    const loggedOut = sessionStorage.getItem("markeemix_logged_out");
    if (loggedOut) {
      sessionStorage.removeItem("markeemix_logged_out");
      setUser(null);
      setPlan("free");
      setLoading(false);
      return;
    }
    
    fetchingRef.current = true;
    
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true,
        headers: { "X-Skip-Auth-Error": "true" }
      });
      const userData = response.data;
      if (!userData) {
        // Anonymous — endpoint returned 200 with null body.
        localStorage.removeItem("markeemix_user");
        setUser(null);
        setPlan("free");
        setError(null);
        return;
      }
      setUser(userData);
      setPlan(userData.plan || "free");
      setError(null);
      
      // Cache user in localStorage for persistence across pages
      localStorage.setItem("markeemix_user", JSON.stringify(userData));
      
      // Also fetch usage limits
      try {
        const usageRes = await axios.get(`${BACKEND_URL}/api/user/usage`, {
          withCredentials: true
        });
        setLimits(usageRes.data.limits);
        if (usageRes.data.plan) setPlan(usageRes.data.plan);
      } catch (e) {
        // Ignore usage fetch errors
      }
    } catch (err) {
      // Session expired or network error — fall back to anonymous.
      if (err.response?.status === 401) {
        localStorage.removeItem("markeemix_user");
        setUser(null);
        setPlan("free");
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  // Fetch user on mount - verify cached session is still valid
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    fetchUser();
  }, [fetchUser]);

  const refresh = useCallback(() => {
    fetchUser(true);
  }, [fetchUser]);

  const isPremium = plan === "pro" || plan === "enterprise" || user?.trial_active === true;
  const isEnterprise = plan === "enterprise";

  const canAccessFeature = (featureName) => {
    if (!limits) return plan !== "free" || user?.trial_active;
    const access = limits[featureName];
    if (typeof access === "boolean") return access;
    if (access === -1) return true;
    return true;
  };

  const value = {
    user,
    plan,
    limits,
    loading,
    error,
    isPremium,
    isEnterprise,
    isLoggedIn: !!user,
    canAccessFeature,
    refresh,
    logout,
    setUserData  // Expose this for login/register to use
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}

export default useUser;
